import React, { useState, useEffect } from 'react'
import { PolarBoxing3D } from './PolarBoxing3D'
import { StreamKeyCard } from '../../components/StreamKeyCard'
import { VideoSrtTab, VideoBurnTab } from '../creators/VideoCreatorTools'
import { ACHIEVEMENTS_LIST, TROPHY_TIERS, getUnlockedAchievements } from './achievements'
import { IconMeditation } from '../../components/ui/Icons'
import { MeditacionTab } from '../meditation/MeditacionTab'

// Encabezado de un grupo de la grilla (Juegos / Apps): título calmo + bajada.
function GroupHeader({ label, caption }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', margin: 0, letterSpacing: -0.2 }}>
        {label}
      </h3>
      {caption && <p style={{ fontSize: 12.5, color: 'var(--text-faint)', margin: '3px 0 0' }}>{caption}</p>}
    </div>
  )
}

// Tarjeta de lanzamiento reutilizable para apps y juegos. Superficie neutra, ícono
// en una caja con tint del color de identidad, y un leve "levantar" al pasar el mouse.
// wide=true → formato destacado horizontal (hero), si no tarjeta vertical en grilla.
function LaunchCard({ kind, accent, glyph, title, desc, cta, onClick, wide = false }) {
  const [hover, setHover] = useState(false)
  const shell = {
    background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 16,
    boxShadow: hover ? '0 10px 26px rgba(0,0,0,0.09)' : '0 1px 3px rgba(0,0,0,0.04)',
    transform: hover ? 'translateY(-2px)' : 'none',
    transition: 'transform .18s ease, box-shadow .18s ease',
  }
  const tile = size => ({
    width: size, height: size, borderRadius: 13, flexShrink: 0,
    background: accent + '1a', color: accent,
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: Math.round(size * 0.48),
  })
  const kindLabel = { fontSize: 10, fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.6 }
  const titleStyle = { fontSize: 16, fontWeight: 700, color: 'var(--text)', margin: '0 0 5px', letterSpacing: -0.2 }
  const descStyle = { fontSize: 12.5, color: 'var(--text-faint)', lineHeight: 1.5, margin: 0 }
  const btn = {
    background: accent, color: '#fff', border: 'none', borderRadius: 10,
    padding: '10px 16px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0,
  }
  const hoverProps = { onMouseEnter: () => setHover(true), onMouseLeave: () => setHover(false) }

  if (wide) {
    return (
      <div {...hoverProps} style={{ ...shell, padding: '16px 18px', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <div style={tile(52)}>{glyph}</div>
        <div style={{ flex: 1, minWidth: 220 }}>
          <span style={{ ...kindLabel, display: 'block', marginBottom: 3 }}>{kind}</span>
          <h4 style={titleStyle}>{title}</h4>
          <p style={descStyle}>{desc}</p>
        </div>
        <button onClick={onClick} style={{ ...btn, marginLeft: 'auto' }}>{cta}</button>
      </div>
    )
  }
  return (
    <div {...hoverProps} style={{ ...shell, padding: 16, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 12 }}>
          <div style={tile(42)}>{glyph}</div>
          <span style={kindLabel}>{kind}</span>
        </div>
        <h4 style={titleStyle}>{title}</h4>
        <p style={descStyle}>{desc}</p>
      </div>
      <button onClick={onClick} style={{ ...btn, marginTop: 16, alignSelf: 'flex-start' }}>{cta}</button>
    </div>
  )
}

export function JuegosTab({ polar, demo, hrv, isFullscreen, setIsFullscreen, onUploadSession, onNavigate, token, user }) {
  const [activeGameId, setActiveGameId] = useState(null)
  const [creatorMode, setCreatorMode] = useState('srt')
  const [unlocked, setUnlocked] = useState({})
  const [trophyFilter, setTrophyFilter] = useState('all') // 'all' | 'unlocked' | 'locked'

  // Cargar logros al montar y al volver al menú
  useEffect(() => {
    setUnlocked(getUnlockedAchievements())
  }, [activeGameId])

  // Si está jugando a Polar Boxing 3D
  if (activeGameId === 'polar_boxing') {
    return (
      <div style={{ maxWidth: 880, margin: '16px auto', padding: '0 12px' }}>
        <PolarBoxing3D
          polar={polar}
          demo={demo}
          hrv={hrv}
          onExit={() => setActiveGameId(null)}
        />
      </div>
    )
  }

  // Si está en la app de Meditación (Coherencia Cardíaca)
  if (activeGameId === 'meditacion') {
    return (
      <div style={{ maxWidth: 880, margin: '16px auto', padding: '0 12px' }}>
        <button onClick={() => setActiveGameId(null)}
          style={{ background: 'var(--chip-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '5px 12px', color: 'var(--text-soft)', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', marginBottom: 10 }}>
          ← Volver a Apps y Juegos
        </button>
        <MeditacionTab
          polar={polar}
          demo={demo}
          hrv={hrv}
          isFullscreen={isFullscreen}
          setIsFullscreen={setIsFullscreen}
          onUploadSession={onUploadSession}
        />
      </div>
    )
  }

  // Si está en Herramientas para creadores (video → subtítulo .srt / quemar overlay)
  if (activeGameId === 'creadores') {
    return (
      <div style={{ maxWidth: 880, margin: '16px auto', padding: '0 12px' }}>
        <button onClick={() => setActiveGameId(null)}
          style={{ background: 'var(--chip-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '5px 12px', color: 'var(--text-soft)', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', marginBottom: 12 }}>
          ← Volver a Apps y Juegos
        </button>
        <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
          {[{ k: 'srt', l: 'Subtítulo (.srt)' }, { k: 'burn', l: 'Quemar / overlay' }, { k: 'obs', l: 'Streaming (OBS)' }].map(t => (
            <button key={t.k} onClick={() => setCreatorMode(t.k)}
              style={{ flex: 1, padding: '8px 0', borderRadius: 9, border: '1px solid var(--border-soft)', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 600, background: creatorMode === t.k ? 'var(--btn-strong)' : 'var(--chip-bg)', color: creatorMode === t.k ? 'var(--seg-active-text-inv)' : 'var(--text-muted)' }}>
              {t.l}
            </button>
          ))}
        </div>
        {creatorMode === 'srt'
          ? <VideoSrtTab token={token} embedded />
          : creatorMode === 'burn'
            ? <VideoBurnTab token={token} />
            : <StreamKeyCard token={token} user={user} />}
      </div>
    )
  }

  const unlockedCount = Object.keys(unlocked).length
  const totalTrophies = ACHIEVEMENTS_LIST.length
  const totalPoints = Object.keys(unlocked).reduce((sum, id) => {
    const ach = ACHIEVEMENTS_LIST.find(a => a.id === id)
    return sum + (ach?.points || 0)
  }, 0)

  const filteredAchievements = ACHIEVEMENTS_LIST.filter(ach => {
    const isUnl = !!unlocked[ach.id]
    if (trophyFilter === 'unlocked') return isUnl
    if (trophyFilter === 'locked') return !isUnl
    return true
  })

  const sensorOn = polar?.status === 'connected' || demo?.active

  return (
    <div style={{ maxWidth: 980, margin: '0 auto', padding: '16px 14px 60px' }}>
      {/* ── 1. Encabezado ── */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        flexWrap: 'wrap', gap: 12, marginBottom: 24
      }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: 'var(--text)', margin: '0 0 4px', letterSpacing: -0.5 }}>
            Apps y Juegos
          </h1>
          <p style={{ fontSize: 13, color: 'var(--text-faint)', margin: 0 }}>
            Entrená tu cuerpo y tu sistema nervioso en tiempo real con el pulso y el acelerómetro de tu Polar H10.
          </p>
        </div>

        {/* Chip de estado del sensor */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 7,
          background: 'var(--field-bg)', border: '1px solid var(--border)',
          borderRadius: 20, padding: '5px 12px'
        }}>
          <span style={{
            width: 7, height: 7, borderRadius: '50%',
            background: sensorOn ? '#30d158' : '#ff9f0a',
            boxShadow: `0 0 6px ${sensorOn ? '#30d158' : '#ff9f0a'}`
          }} />
          <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text)' }}>
            {polar?.status === 'connected' ? 'Polar H10 conectado' : demo?.active ? 'Modo demo activo' : 'Sin sensor (teclado / táctil)'}
          </span>
        </div>
      </div>

      {/* ── 2. Juegos ── */}
      <section style={{ marginBottom: 28 }}>
        <GroupHeader
          label="Juegos"
          caption="Movete de verdad: el acelerómetro y el pulso del H10 son el control."
        />
        <LaunchCard
          wide
          kind="Juego · Ring 3D en Three.js"
          accent="#ff3b30"
          glyph="🥊"
          title="Polar Boxing 3D"
          desc="Escenografía de estadio con multitud y flashes. Esquivá con el torso, hacé footwork en punta de pie y meté contragolpes secos con el pecho."
          cta="Subir al ring"
          onClick={() => setActiveGameId('polar_boxing')}
        />
      </section>

      {/* ── 3. Apps ── */}
      <section style={{ marginBottom: 32 }}>
        <GroupHeader
          label="Apps"
          caption="Respiración, video y streaming conectados a tu pulso."
        />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
          <LaunchCard
            kind="App · Respiración"
            accent="#8b5cf6"
            glyph={<IconMeditation size={20} color="#8b5cf6" />}
            title="Coherencia Cardíaca (Zen)"
            desc="Sincronizá tu respiración a 5.5 rpm siguiendo el orbe. Entrená tu parasimpático en vivo para subir tu HRV y bajar el estrés."
            cta="Empezar a respirar"
            onClick={() => setActiveGameId('meditacion')}
          />
          <LaunchCard
            kind="App · Video"
            accent="#007aff"
            glyph="🎬"
            title="Herramientas para Creadores"
            desc="Subtítulos .srt con tu pulso, overlay de FC quemado al video y streaming en vivo a OBS (Twitch/YouTube). Todo en tu navegador."
            cta="Abrir herramientas"
            onClick={() => setActiveGameId('creadores')}
          />
        </div>
      </section>

      {/* ── 4. Sala de Trofeos y Sistema de Logros ── */}
      <div style={{
        background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 18,
        padding: '20px 18px', boxShadow: '0 2px 8px rgba(0,0,0,0.03)'
      }}>
        {/* Cabecera de la Sala de Trofeos */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          flexWrap: 'wrap', gap: 12, marginBottom: 16
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <span style={{ fontSize: 16 }}>★</span>
              <h3 style={{ fontSize: 16, fontWeight: 800, color: 'var(--text)', margin: 0, letterSpacing: -0.2 }}>
                Sala de Trofeos y Logros
              </h3>
            </div>
            <span style={{ fontSize: 11.5, color: 'var(--text-faint)', display: 'block', marginTop: 2 }}>
              {unlockedCount} de {totalTrophies} Trofeos Desbloqueados · {totalPoints} Puntos acumulados
            </span>
          </div>

          {/* Filtros de trofeos */}
          <div style={{ display: 'flex', background: 'var(--field-bg)', borderRadius: 8, padding: 2, border: '1px solid var(--border-soft)' }}>
            <button
              onClick={() => setTrophyFilter('all')}
              style={{
                background: trophyFilter === 'all' ? 'var(--card-bg)' : 'transparent',
                color: trophyFilter === 'all' ? 'var(--text)' : 'var(--text-faint)',
                border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit'
              }}
            >
              Todos ({totalTrophies})
            </button>
            <button
              onClick={() => setTrophyFilter('unlocked')}
              style={{
                background: trophyFilter === 'unlocked' ? 'var(--card-bg)' : 'transparent',
                color: trophyFilter === 'unlocked' ? 'var(--text)' : 'var(--text-faint)',
                border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit'
              }}
            >
              Desbloqueados ({unlockedCount})
            </button>
            <button
              onClick={() => setTrophyFilter('locked')}
              style={{
                background: trophyFilter === 'locked' ? 'var(--card-bg)' : 'transparent',
                color: trophyFilter === 'locked' ? 'var(--text)' : 'var(--text-faint)',
                border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit'
              }}
            >
              Por Desbloquear ({totalTrophies - unlockedCount})
            </button>
          </div>
        </div>

        {/* Barra de Progreso General */}
        <div style={{ marginBottom: 18 }}>
          <div style={{ height: 8, background: 'var(--track)', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{
              width: `${Math.round((unlockedCount / totalTrophies) * 100)}%`, height: '100%',
              background: 'linear-gradient(90deg, #007aff, #ffd700)', borderRadius: 4,
              transition: 'width 0.4s ease'
            }} />
          </div>
        </div>

        {/* Grid de Trofeos */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 10 }}>
          {filteredAchievements.map(ach => {
            const isUnl = !!unlocked[ach.id]
            const tierMeta = TROPHY_TIERS[ach.tier] || TROPHY_TIERS.bronze
            return (
              <div
                key={ach.id}
                style={{
                  background: isUnl ? tierMeta.bg : 'var(--field-bg)',
                  border: `1px solid ${isUnl ? tierMeta.color + '55' : 'var(--border-soft)'}`,
                  borderRadius: 12, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12,
                  opacity: isUnl ? 1 : 0.65, transition: 'all 0.2s ease'
                }}
              >
                {/* Medalla / Icono */}
                <div style={{
                  width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                  background: isUnl ? tierMeta.color : 'rgba(255,255,255,0.08)',
                  color: isUnl ? '#000' : 'var(--text-faint)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 16, fontWeight: 900,
                  boxShadow: isUnl ? `0 0 12px ${tierMeta.color}66` : 'none'
                }}>
                  ★
                </div>

                {/* Texto del Trofeo */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                    <span style={{ fontSize: 13, fontWeight: 800, color: isUnl ? 'var(--text)' : 'var(--text-muted)' }}>
                      {ach.title}
                    </span>
                    <span style={{
                      fontSize: 9.5, fontWeight: 800, color: tierMeta.color,
                      background: isUnl ? `${tierMeta.color}25` : 'transparent',
                      padding: '1px 5px', borderRadius: 4
                    }}>
                      {tierMeta.name} · +{ach.points}
                    </span>
                  </div>
                  <p style={{ fontSize: 11, color: 'var(--text-faint)', margin: '2px 0 0', lineHeight: 1.35 }}>
                    {ach.subtitle}
                  </p>
                  {isUnl && (
                    <span style={{ fontSize: 9.5, color: '#30d158', fontWeight: 700, display: 'block', marginTop: 4 }}>
                      ✓ Desbloqueado el {new Date(unlocked[ach.id].unlockedAt).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
